import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { categories, features, comparisonData, steps, securityItems, comparisonLinks, articlePreviews, shopCategories } from '@/mocks/categories';

export default function Home() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [dropdownOpen]);

  const siteUrl = 'https://vigorbuyspread.fun';
  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'VigorBuy Spreadsheet',
    url: siteUrl,
    description: 'The complete resource for VigorBuy Spreadsheet guides, templates, comparisons, and data management tips.',
    sameAs: [
      'https://kaspreadsheet.com',
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative w-full min-h-[520px] md:min-h-[640px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Clean%20modern%20workspace%20with%20elegant%20laptop%20displaying%20colorful%20organized%20spreadsheet%20data%20and%20charts%2C%20minimalist%20white%20desk%20with%20notebook%20and%20potted%20plant%2C%20warm%20natural%20sunlight%20from%20large%20window%2C%20soft%20cream%20and%20sage%20green%20color%20palette%2C%20professional%20yet%20inviting%20atmosphere%2C%20editorial%20photography%20style%2C%20shallow%20depth%20of%20field%2C%20modern%20Scandinavian%20office%20design%2C%20clean%20geometric%20shapes&width=1600&height=900&seq=hero-vigorbuy-v2-2026&orientation=landscape"
            alt="VigorBuy Spreadsheet workspace"
            className="w-full h-full object-cover object-top"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/35"></div>
        </div>
        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center py-16 md:py-20">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50/90 backdrop-blur-sm text-xs md:text-sm font-medium text-foreground-700 mb-6">
            <i className="ri-verified-badge-line text-primary-500"></i>
            <span>Complete Spreadsheet Guides</span>
            <span className="w-1 h-1 rounded-full bg-accent-400 mx-0.5"></span>
            <span>Updated 2026</span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight mb-4 md:mb-6">
            VigorBuy Spreadsheet
            <br />
            <span className="text-primary-300">The Complete Data Management Solution</span>
            <br />
            <span className="text-lg md:text-2xl lg:text-3xl font-medium text-background-200"> &amp; Ultimate User Guide</span>
          </h1>
          <p className="text-sm md:text-lg text-background-200 max-w-2xl mx-auto mb-8 leading-relaxed">
            Discover powerful VigorBuy Spreadsheet templates, tracking methods, optimization tips, comparisons, and expert guides designed to help users organize products, prices, orders, and data efficiently.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <Link
              to="/vigorbuy-spreadsheet-guide"
              className="w-full sm:w-auto px-6 md:px-8 py-3 md:py-3.5 text-sm md:text-base font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-all whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/25"
            >
              Explore VigorBuy Spreadsheet Guide
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto px-6 md:px-8 py-3 md:py-3.5 text-sm md:text-base font-semibold text-foreground-800 bg-background-50 hover:bg-background-100 rounded-lg transition-all whitespace-nowrap cursor-pointer border border-background-200/50"
            >
              Visit Official Website
            </a>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mt-8 text-xs text-background-300">
            <span className="flex items-center gap-1.5"><i className="ri-check-line text-primary-400"></i> Complete Spreadsheet Guides</span>
            <span className="flex items-center gap-1.5"><i className="ri-check-line text-primary-400"></i> Data Management Resources</span>
            <span className="flex items-center gap-1.5"><i className="ri-check-line text-primary-400"></i> Updated Information</span>
            <span className="flex items-center gap-1.5"><i className="ri-check-line text-primary-400"></i> Secure Usage Tips</span>
          </div>
        </div>
      </section>

      {/* What Is VigorBuy Spreadsheet - with images */}
      <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-3">Overview</span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">What Is VigorBuy Spreadsheet?</h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-3xl mx-auto leading-relaxed">
              VigorBuy Spreadsheet is a structured spreadsheet solution designed to help users organize, analyze, and manage VigorBuy related information efficiently. Whether you are tracking products, monitoring prices, or managing purchase records, this system provides a clean, organized approach to data management.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {[
              { icon: 'ri-shopping-cart-line', label: 'Product Tracking', img: 'https://readdy.ai/api/search-image?query=Organized%20product%20catalog%20displayed%20on%20clean%20minimalist%20desk%20with%20laptop%20showing%20inventory%20spreadsheet%2C%20warm%20lighting%2C%20cream%20and%20beige%20tones%2C%20professional%20product%20photography%20aesthetic%2C%20soft%20focus%2C%20editorial%20style&width=400&height=320&seq=overview-tracking-prod&orientation=landscape' },
              { icon: 'ri-money-dollar-circle-line', label: 'Price Monitoring', img: 'https://readdy.ai/api/search-image?query=Modern%20desk%20with%20laptop%20displaying%20price%20comparison%20charts%20and%20graphs%2C%20calculator%20nearby%2C%20warm%20natural%20light%2C%20cream%20and%20soft%20green%20accents%2C%20professional%20finance%20aesthetic%2C%20clean%20minimalist%20setup&width=400&height=320&seq=overview-price-mon&orientation=landscape' },
              { icon: 'ri-folder-chart-line', label: 'Data Organization', img: 'https://readdy.ai/api/search-image?query=Neatly%20organized%20workspace%20with%20color-coded%20folders%20and%20laptop%20showing%20structured%20spreadsheet%20data%2C%20warm%20sunlight%2C%20minimalist%20Scandinavian%20design%2C%20cream%20and%20warm%20beige%20tones%2C%20professional%20organization%20theme&width=400&height=320&seq=overview-data-org&orientation=landscape' },
              { icon: 'ri-file-list-2-line', label: 'Purchase Management', img: 'https://readdy.ai/api/search-image?query=Clean%20desk%20with%20laptop%20showing%20order%20management%20dashboard%2C%20receipts%20neatly%20arranged%2C%20warm%20coffee%20cup%2C%20soft%20natural%20lighting%2C%20cream%20and%20beige%20aesthetic%2C%20professional%20yet%20cozy%20workspace%2C%20editorial%20style&width=400&height=320&seq=overview-purchase-mgmt&orientation=landscape' },
              { icon: 'ri-search-eye-line', label: 'Research Workflow', img: 'https://readdy.ai/api/search-image?query=Research%20workspace%20with%20laptop%20displaying%20data%20analysis%20screen%2C%20notebook%20with%20handwritten%20notes%2C%20warm%20desk%20lamp%20glow%2C%20cream%20and%20sage%20green%20tones%2C%20minimalist%20study%20setup%2C%20professional%20academic%20aesthetic&width=400&height=320&seq=overview-research-wf&orientation=landscape' },
            ].map((item) => (
              <div key={item.label} className="flex flex-col rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-colors overflow-hidden cursor-default">
                <div className="w-full h-32 md:h-36 overflow-hidden">
                  <img src={item.img} alt={item.label} className="w-full h-full object-cover object-top" loading="lazy" />
                </div>
                <div className="flex flex-col items-center gap-2 p-3 md:p-4">
                  <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-primary-100">
                    <i className={`${item.icon} text-primary-600 text-base md:text-lg`}></i>
                  </div>
                  <span className="text-xs md:text-sm font-medium text-foreground-700 text-center whitespace-nowrap">{item.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section id="comparison" className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full mb-3">Comparison</span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">Why Choose VigorBuy Spreadsheet?</h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
              See how VigorBuy Spreadsheet transforms your data management workflow compared to traditional methods.
            </p>
          </div>
          <div className="overflow-x-auto rounded-xl border border-background-200 bg-background-50">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100">
                  <th className="text-left px-4 md:px-6 py-3 md:py-4 font-heading font-semibold text-foreground-800 text-sm">Challenge</th>
                  <th className="text-left px-4 md:px-6 py-3 md:py-4 font-heading font-semibold text-foreground-600 text-sm">Traditional Method</th>
                  <th className="text-left px-4 md:px-6 py-3 md:py-4 font-heading font-semibold text-primary-600 text-sm">VigorBuy Spreadsheet</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, i) => (
                  <tr key={row.problem} className={`border-b border-background-100 ${i % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}`}>
                    <td className="px-4 md:px-6 py-3 md:py-3.5 font-medium text-foreground-800">{row.problem}</td>
                    <td className="px-4 md:px-6 py-3 md:py-3.5 text-foreground-500">
                      <span className="inline-flex items-center gap-1.5">
                        <i className="ri-close-circle-line text-accent-400"></i>
                        {row.traditional}
                      </span>
                    </td>
                    <td className="px-4 md:px-6 py-3 md:py-3.5 text-primary-700 font-medium">
                      <span className="inline-flex items-center gap-1.5">
                        <i className="ri-checkbox-circle-line text-primary-500"></i>
                        {row.vigorbuy}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full mb-3">Features</span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">VigorBuy Spreadsheet Features</h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
              Powerful tools built into every VigorBuy Spreadsheet to help you work smarter.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {features.map((feat) => (
              <div key={feat.title} className="group p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all cursor-default">
                <div className="w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-lg bg-secondary-100 mb-4 group-hover:bg-primary-100 transition-colors">
                  <i className={`${feat.icon} text-secondary-600 group-hover:text-primary-600 text-xl md:text-2xl transition-colors`}></i>
                </div>
                <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2">{feat.title}</h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{feat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Shop Categories with Images */}
      <section id="categories" className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full mb-3">Shop Categories</span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">Browse Product Categories</h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto mb-6">
              Explore our complete range of products. Select a category below or use the dropdown to navigate directly to our official store.
            </p>

            {/* Dropdown Selector */}
            <div ref={dropdownRef} className="relative inline-block text-left w-full max-w-xs mx-auto mb-6">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-foreground-800 bg-background-50 border border-background-300 rounded-lg hover:border-primary-300 transition-colors cursor-pointer"
              >
                <span>Select a Category</span>
                {dropdownOpen ? (
                  <i className="ri-arrow-up-s-line text-foreground-500"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-foreground-500"></i>
                )}
              </button>
              {dropdownOpen && (
                <div className="absolute z-40 mt-1 w-full bg-background-50 border border-background-200 rounded-lg shadow-sm max-h-64 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setDropdownOpen(false)}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Category Cards with Images */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-3" data-product-shop="">
              {shopCategories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group flex flex-col rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 overflow-hidden transition-all cursor-pointer"
                >
                  <div className="w-full aspect-square overflow-hidden bg-background-100">
                    <img src={cat.image} alt={cat.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                  </div>
                  <div className="flex items-center justify-center gap-1.5 px-2 py-2.5">
                    <i className="ri-arrow-right-up-line text-primary-400 text-xs"></i>
                    <span className="text-xs font-medium text-foreground-700 group-hover:text-primary-600 transition-colors whitespace-nowrap">{cat.name}</span>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto px-8 py-3 text-sm md:text-base font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-all whitespace-nowrap cursor-pointer"
            >
              VigorBuy Spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto px-8 py-3 text-sm md:text-base font-semibold text-background-50 bg-accent-500 hover:bg-accent-600 rounded-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>
        </div>
      </section>

      {/* SEO Articles Grid - 9 articles with thumbnails */}
      <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-3">Guides & Articles</span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">VigorBuy Spreadsheet Knowledge Base</h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
              Explore our complete library of guides, tutorials, and resources to master VigorBuy Spreadsheet.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {articlePreviews.map((article) => (
              <Link
                key={article.slug}
                to={`/${article.slug}`}
                className="group flex flex-col rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 overflow-hidden transition-all cursor-pointer"
              >
                <div className="w-full h-44 md:h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                    loading="lazy"
                  />
                </div>
                <div className="p-4 md:p-5 flex flex-col flex-grow">
                  <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors leading-snug">
                    {article.title}
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed flex-grow mb-3">
                    {article.excerpt}
                  </p>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 whitespace-nowrap">
                    Read Article <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full mb-3">Process</span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">How VigorBuy Spreadsheet Works</h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
              A simple four-step process to transform your data into actionable insights.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {steps.map((step, i) => (
              <div key={step.number} className="relative p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70 text-center">
                <span className="absolute -top-3 -left-3 w-7 h-7 rounded-full bg-primary-500 text-background-50 text-xs font-bold flex items-center justify-center">{step.number}</span>
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-4">
                  <i className={`${step.icon} text-primary-600 text-xl`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{step.title}</h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{step.description}</p>
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <i className="ri-arrow-right-line text-foreground-300 text-xl"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Hub */}
      <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-3">Resources</span>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">VigorBuy Spreadsheet Comparison Hub</h2>
          <p className="text-sm md:text-base text-foreground-600 mb-8">
            Explore detailed comparisons and resources to make the most of your VigorBuy Spreadsheet experience.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            {comparisonLinks.map((link) => (
              <Link
                key={link.name}
                to={link.url}
                className="flex items-center justify-between p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
              >
                <span className="text-sm font-medium text-foreground-800">{link.name}</span>
                <i className="ri-arrow-right-line text-primary-400"></i>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Security & Privacy */}
      <section id="security" className="w-full py-14 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full mb-3">Security</span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">Is VigorBuy Spreadsheet Safe?</h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-3xl mx-auto leading-relaxed">
              Your data security matters. VigorBuy Spreadsheet follows best practices to ensure your information stays protected. From password protection to regular backups, we prioritize the safety of your spreadsheet data.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 mb-8">
            {[
              { icon: 'ri-shield-check-line', title: 'Data Protection', desc: 'Built-in safeguards keep your spreadsheet data secure from unauthorized access.' },
              { icon: 'ri-lock-line', title: 'Safe Practices', desc: 'Follow industry-standard spreadsheet security protocols for peace of mind.' },
              { icon: 'ri-eye-off-line', title: 'Privacy Awareness', desc: 'Control who sees your data with granular sharing and visibility settings.' },
              { icon: 'ri-share-forward-line', title: 'Secure Sharing', desc: 'Share spreadsheets safely using encrypted transfer methods and access controls.' },
            ].map((item) => (
              <div key={item.title} className="flex gap-4 p-4 md:p-5 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100">
                  <i className={`${item.icon} text-accent-600 text-lg`}></i>
                </div>
                <div>
                  <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-1">{item.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="overflow-x-auto rounded-xl border border-background-200 bg-background-50">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100">
                  <th className="text-left px-4 md:px-6 py-3 md:py-4 font-heading font-semibold text-foreground-800">Security Method</th>
                  <th className="text-left px-4 md:px-6 py-3 md:py-4 font-heading font-semibold text-foreground-800">Benefit</th>
                </tr>
              </thead>
              <tbody>
                {securityItems.map((item, i) => (
                  <tr key={item.method} className={`border-b border-background-100 ${i % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}`}>
                    <td className="px-4 md:px-6 py-3 md:py-3.5 font-medium text-foreground-800">
                      <span className="inline-flex items-center gap-1.5">
                        <i className="ri-shield-check-line text-primary-500"></i>
                        {item.method}
                      </span>
                    </td>
                    <td className="px-4 md:px-6 py-3 md:py-3.5 text-foreground-600">{item.benefit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="w-full py-14 md:py-20 px-4 md:px-6 bg-primary-500">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-4">
            Ready to Master VigorBuy Spreadsheet?
          </h2>
          <p className="text-sm md:text-base text-primary-100 mb-8 leading-relaxed">
            Dive into our comprehensive guide covering everything from beginner basics to advanced techniques. Learn templates, comparisons, security tips, and more — all in one place.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/vigorbuy-spreadsheet-guide"
              className="w-full sm:w-auto px-6 md:px-8 py-3 md:py-3.5 text-sm md:text-base font-semibold text-primary-600 bg-background-50 hover:bg-background-100 rounded-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Read the Full Guide
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto px-6 md:px-8 py-3 md:py-3.5 text-sm md:text-base font-semibold text-background-50 border border-background-50/30 hover:bg-background-50/10 rounded-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Visit Official Store
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}